import { useState, useCallback } from 'react';
import type { ChatMessage, InstitutionCorrespondence } from '../../../types';
import type { WorkflowDocument } from '@/lib/services/correspondences/workflows/workflow-types';

interface UseMessageSendingProps {
  allCorrespondence: InstitutionCorrespondence[];
  messageSubject: string;
  pendingAttachments: WorkflowDocument[];
  existingDraftId: string | null;
  onSendMessage?: (
    content: string,
    subject?: string,
    attachments?: WorkflowDocument[],
    parentCorrespondenceId?: string | number,
    existingDraftId?: number
  ) => void | Promise<void>;
  onClearAttachments: () => void;
  onClearDraft: () => void;
}

export function useMessageSending({
  allCorrespondence,
  messageSubject,
  pendingAttachments,
  existingDraftId,
  onSendMessage,
  onClearAttachments,
  onClearDraft
}: UseMessageSendingProps) {
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [optimisticMessage, setOptimisticMessage] =
    useState<ChatMessage | null>(null);

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (isSendingMessage) return;
      setIsSendingMessage(true);

      // Inject optimistic message into the thread immediately
      setOptimisticMessage({
        id: 'sending-optimistic',
        content,
        sender: 'You',
        timestamp: new Date(),
        direction: 'outbound',
        status: 'sent',
        type: 'reply',
        isRead: true,
        isSending: true,
        subject: messageSubject,
        attachments: pendingAttachments.map((att) => ({
          id: att.id,
          filename: att.displayName || att.originalFilename,
          mimeType: att.mimeType || 'application/octet-stream',
          size: att.fileSizeKb ? att.fileSizeKb * 1024 : undefined,
          url: att.s3Key || undefined
        }))
      });

      try {
        // Find most recent inbound correspondence
        const inboundCorrespondences = allCorrespondence.filter(
          (c) =>
            c.direction === 'inbound' &&
            (c.status === 'received' || c.status === 'response-received')
        );

        // Determine parent correspondence ID
        const parentCorrespondenceId =
          inboundCorrespondences.length > 0
            ? inboundCorrespondences[inboundCorrespondences.length - 1].id
            : allCorrespondence.length > 0
              ? allCorrespondence.filter((c) => c.status !== 'draft')[
                  allCorrespondence.filter((c) => c.status !== 'draft').length -
                    1
                ]?.id
              : undefined;

        if (onSendMessage) {
          await onSendMessage(
            content,
            messageSubject,
            pendingAttachments,
            parentCorrespondenceId,
            existingDraftId ? parseInt(existingDraftId) : undefined
          );
        }

        // Clear draft ID before clearing attachments to prevent
        // useDraftAttachmentSync from saving empty attachments to the
        // already-sent correspondence via stale draft ID
        onClearDraft();
        onClearAttachments();
      } catch {
        // Send failed — remove optimistic message
        setOptimisticMessage(null);
      } finally {
        setIsSendingMessage(false);
      }
    },
    [
      isSendingMessage,
      allCorrespondence,
      messageSubject,
      pendingAttachments,
      existingDraftId,
      onSendMessage,
      onClearAttachments,
      onClearDraft
    ]
  );

  const clearOptimisticMessage = useCallback(() => {
    setOptimisticMessage(null);
  }, []);

  return {
    isSendingMessage,
    optimisticMessage,
    clearOptimisticMessage,
    handleSendMessage
  };
}
